import React from "react";
import { getHashParam, setHashParam } from "../../lib/hashState";
import type { ActiveFilter } from "./util";

const FILTER_KEYS = [
  "user",
  "team",
  "repo",
  "model",
  "kind",
  "client",
  "tenant",
  "feature",
  "env",
  "key",
  "device",
];

/** Filters ride in the hash as f=key:value:label,… so links keep them. */
function readFilters(): ActiveFilter[] {
  const raw = getHashParam("f");
  if (!raw) return [];
  return raw
    .split(",")
    .map((part) => part.split(":").map(decodeURIComponent))
    .filter(([k, v]) => FILTER_KEYS.includes(k) && v)
    .map(([k, v, label]) => ({
      key: k as ActiveFilter["key"],
      value: v,
      label: label || v,
    }));
}

export function writeFilters(f: ActiveFilter[]) {
  setHashParam(
    "f",
    f.length
      ? f
          .map((x) =>
            [x.key, x.value, x.label].map(encodeURIComponent).join(":"),
          )
          .join(",")
      : null,
  );
}

export function useInsightFilters(initial?: ActiveFilter[]) {
  const [filters, setFiltersState] = React.useState<ActiveFilter[]>(() =>
    initial?.length ? initial : readFilters(),
  );
  const setFilters = React.useCallback(
    (value: ActiveFilter[] | ((current: ActiveFilter[]) => ActiveFilter[])) => {
      setFiltersState((current) => {
        const next = typeof value === "function" ? value(current) : value;
        writeFilters(next);
        return next;
      });
    },
    [],
  );
  React.useEffect(() => {
    if (initial?.length) writeFilters(initial);
  }, [initial]);
  React.useEffect(() => {
    const sync = () => setFiltersState(readFilters());
    window.addEventListener("popstate", sync);
    window.addEventListener("hashchange", sync);
    return () => {
      window.removeEventListener("popstate", sync);
      window.removeEventListener("hashchange", sync);
    };
  }, []);
  const onDrill = React.useCallback(
    (filter: ActiveFilter) =>
      setFilters((current) => [
        ...current.filter((f) => f.key !== filter.key),
        filter,
      ]),
    [setFilters],
  );
  return { filters, setFilters, onDrill };
}
